import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShoppingCart, Minus, Plus, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return <div className="mx-auto max-w-3xl px-5 py-32 text-center text-zinc-400">Card not found. <Link to="/catalog" className="text-secondary">Back to shop</Link></div>;
  if (!product) return <div className="mx-auto max-w-6xl px-5 py-20"><div className="h-[520px] animate-pulse rounded-2xl bg-zinc-900" /></div>;

  return (
    <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8">
      <Link to="/catalog" className="mb-8 inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors hover:text-white" data-testid="back-btn">
        <ArrowLeft size={16} /> Back to shop
      </Link>
      <div className="grid gap-10 md:grid-cols-2">
        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
          className="group relative overflow-hidden rounded-3xl border border-white/10 bg-zinc-900">
          <img src={product.image} alt={product.name} className="aspect-[4/5] w-full object-cover" />
          <div className="foil-shine pointer-events-none absolute inset-0" />
        </motion.div>

        <div className="flex flex-col">
          <span className="overline text-secondary">{product.rarity} · {product.category}</span>
          {product.grade ? (
            <span className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full border border-amber-400/60 bg-amber-400/10 px-3 py-1 text-sm font-700 text-amber-300" data-testid="detail-grade">
              <ShieldCheck size={14} /> Graded {product.grade}
            </span>
          ) : null}
          <h1 className="mt-3 font-display text-3xl font-900 tracking-tight text-white sm:text-4xl" data-testid="product-title">{product.name}</h1>
          <p className="mt-4 text-zinc-400">{product.description}</p>
          <div className="mt-6 font-display text-4xl font-900 text-white" data-testid="product-price">${product.price.toFixed(2)}</div>
          <p className={`mt-2 text-sm ${product.stock > 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="product-stock">
            {product.stock > 0 ? `${product.stock} in stock` : "Sold out"}
          </p>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center rounded-full border border-white/10">
              <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus" className="p-3 text-zinc-400 hover:text-white"><Minus size={16} /></button>
              <span className="w-8 text-center font-medium text-white" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} data-testid="qty-plus" className="p-3 text-zinc-400 hover:text-white"><Plus size={16} /></button>
            </div>
            <button data-testid="detail-add-to-cart" disabled={product.stock <= 0}
              onClick={() => { add(product, qty); toast.success(`Added ${qty} × ${product.name}`); }}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 font-medium text-white transition-transform hover:-translate-y-1 disabled:opacity-40 disabled:hover:translate-y-0">
              <ShoppingCart size={18} /> Add to cart
            </button>
          </div>

          <div className="mt-8 flex items-center gap-3 rounded-2xl border border-white/10 bg-card p-4 text-sm text-zinc-400">
            <ShieldCheck size={20} className="text-secondary" /> Authenticity guaranteed · protected shipping worldwide
          </div>
        </div>
      </div>
    </div>
  );
}
